import { Alert, Box, Button, CircularProgress, FormControl, FormControlLabel, FormLabel, Grid, IconButton, Radio, RadioGroup, TextField, Typography } from "@mui/material"
import { Controller, useFormContext } from "react-hook-form"
import BasicDatePicker from "../../../../../../../components/BasicDatePicker"
import { formatarDecimalNumber, formatarInputValorMonetário, formatarMonetárioParaNúmero } from "../../../../../../../helpers/masks"
import { useCallback, useEffect, useState } from "react"
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import moment, { Moment } from "moment"
import { useMutation } from "react-query"
import axiosClient from "../../../../../../../lib/axios"

export default function Step02() {

  const { watch, setValue, getValues, control, handleSubmit, setError } = useFormContext()

  const handleNext = getValues('funcoesAux.onNext')
  const handleBack = getValues('funcoesAux.onBack')
  const parcelas: TParcela[] = watch('step02.parcelas')
  const valor: string = watch('step02.valor_total')
  const valores: TInternFilters = watch('step02')

  const [gerarParcelas, setGerarParcelas] = useState<TObjParcelas>({
    quantidade: '2',
    intervalo: '30',
    data_inicial: moment()
  });

  //Valores importantes
  const valoresSelect = [{ value: 'false', label: 'Não' }, { value: 'true', label: 'Sim' }]
  //

  //Funções manipuladoras
  const alterarValorTotal = useCallback(() => {
    let prevValue = parcelas
    const numeroParcelas = parcelas.length
    const valorTotal = formatarMonetárioParaNúmero(valor)

    let valorParcelaFormatado: string
    let ultimaParcela: string = '0'
    let ultimaDataVencimento: Moment = valores.data_vencimento

    function calcularValoresParcelasLocal(valorTotal: number, arrayLenght: number) {
      const valorParcela = Math.floor((valorTotal / (arrayLenght + 1)) * 100) / 100;
      const valorUltimaParcela = formatarInputValorMonetário(formatarDecimalNumber(valorParcela + (valorTotal - (valorParcela * (arrayLenght + 1)))))
      const valorParcelaFormatado = formatarInputValorMonetário(formatarDecimalNumber(valorParcela))
      return { valorParcela, valorUltimaParcela, valorParcelaFormatado }
    }

    if (numeroParcelas > 0) {
      const valores = calcularValoresParcelasLocal(valorTotal, numeroParcelas)
      ultimaParcela = valores.valorUltimaParcela;
      valorParcelaFormatado = valores.valorParcelaFormatado;
      ultimaDataVencimento = prevValue[prevValue.length - 1].data_vencimento
    }
    const newParcela = {
      numero_parcela: prevValue.length + 1,
      valor_parcela: prevValue.length === 0 ? valor : ultimaParcela,
      data_vencimento: prevValue.length === 0 ? valores.data_vencimento : ultimaDataVencimento.clone().add(1, 'months')
    }

    prevValue = prevValue.map((value) => {
      return { ...value, valor_parcela: valorParcelaFormatado }
    })

    return [...prevValue, newParcela]

  }, [valor, parcelas, valores])

  const handleChangeGerarParcelaInput = (key: keyof TObjParcelas, value: string | Moment | null) => {
    setGerarParcelas(prevState => ({ ...prevState, [key]: value }))
  }

  const handleAdicionarParcela = () => {
    const parcelasAtualizadas = alterarValorTotal()
    atualizarParcelas(parcelasAtualizadas)
  }

  const handleGerarParcelas = (value: TObjParcelas) => {
    const data_inicial = value.data_inicial
    const quantidade = Number(value.quantidade)
    const intervalo = Number(value.intervalo)

    const valorTotal = formatarMonetárioParaNúmero(valor)

    const valores = calcularValoresParcelas(valorTotal, quantidade);

    const parcelas: TParcela[] = Array.from({ length: quantidade }, (_, index) => {
      const numero_parcela = index + 1;
      const data_vencimento = moment(data_inicial).add(intervalo * index, 'days');

      return {
        numero_parcela,
        data_vencimento,
        valor_parcela: index === quantidade - 1 ? formatarInputValorMonetário(valores.valorUltimaParcela) : formatarInputValorMonetário(formatarDecimalNumber(valores.valorParcela))
      };
    });

    atualizarParcelas(parcelas)
  }

  const handleDeletarParcela = () => {
    if (parcelas.length > 1) {
      const newParcelas = parcelas.slice(0, -1);

      const total = formatarMonetárioParaNúmero(valor);
      const numeroParcelas = newParcelas.length;

      const valores = calcularValoresParcelas(total, numeroParcelas);

      const parcelasAtualizadas = newParcelas.map((parcela, index) => ({
        ...parcela,
        valor_parcela: index === numeroParcelas - 1 ? formatarInputValorMonetário(valores.valorUltimaParcela) : formatarInputValorMonetário(formatarDecimalNumber(valores.valorParcela))
      }));

      atualizarParcelas(parcelasAtualizadas)
    }
  }

  const handleAlterarValorParcela = (index: number, valor: string) => {
    const newParcelas = [...parcelas]
    newParcelas[index].valor_parcela = valor

    atualizarParcelas(newParcelas)
  }

  const handleAlterarDataVencimento = (index: number, data: Moment | null) => {
    const newParcelas = [...parcelas]
    newParcelas[index].data_vencimento = data || moment()

    atualizarParcelas(newParcelas)
  }

  const atualizarParcelas = (parcelaAtualizada: TParcela[]) => {
    setValue('step02.parcelas', parcelaAtualizada)
  }

  const handleValidateInputs = (data: any) => {
    const { step02 } = data

    const requiredKeys = ['data_emissao', 'data_vencimento', 'valor_total', 'is_parcelado', 'parcelas'];

    const camposPreenchidos = requiredKeys.every((key) => {
      const validacao = step02[key] && step02[key] !== ''
      if (!validacao) {
        setError(`step02.${key}`, {
          message: 'Este campo é obrigatório',
          type: 'error'
        })
        return false;
      }
      return validacao;
    });
    if (!camposPreenchidos) return false;

    const total = formatarMonetárioParaNúmero(step02.valor_total);
    if (total <= 0) {
      setError(`step02.valor_total`, {
        message: 'O valor total não pode se zero',
        type: 'error'
      })
      return false;
    }

    let parcelasValidas = true;
    if (step02.is_parcelado === 'true') {
      parcelasValidas = step02.parcelas.every((parcela: TParcela) => {
        return !!parcela.valor_parcela && !!parcela.data_vencimento;
      });

      if (!parcelasValidas) {
        setError(`step02.parcelas`, {
          message: 'Todos os campos de parcela devem ser preenchidos',
          type: 'error'
        })
        return false;
      }

      const somaParcelas = parseFloat((step02.parcelas.reduce((prev: number, curr: TParcela) => prev + formatarMonetárioParaNúmero((curr.valor_parcela)), 0)).toFixed(2))
      const total = formatarMonetárioParaNúmero(step02.valor_total);

      if (somaParcelas !== total) {
        setError(`step02.parcelas`, {
          message: 'A soma dos valores das parcelas não corresponde ao valor total',
          type: 'error'
        })
        return false;
      }
      if (somaParcelas <= 0) {
        setError(`step02.parcelas`, {
          message: 'O valor das parcelas não podem se zero',
          type: 'error'
        })
        return false;
      }
    }

    // Validação da data de vencimento
    if (step02.is_parcelado === 'false' && moment(step02.data_vencimento).isBefore(moment(), 'day')) {
      setError('step02.data_vencimento', {
        message: 'Vencimento não pode ser menor que a data atual',
        type: 'error'
      });
      return false;
    }

    return true;
  }

  const { mutateAsync: validacaoData, isLoading: isLoadingValidacaoData } = useMutation({
    mutationKey: 'validacao-data',
    mutationFn: (inputs: Moment) => axiosClient.post(`/validacao-data`, inputs, {
      params: {
        data: inputs?.format("DD-MM-YYYY"),
      },
    }),
  });

  const handleNextStep = async (data: any) => {
    if (!handleValidateInputs(data)) return;

    handleNext();
  }
  //

  //UseEffect para atualizar as parcelas de acordo com o valor total
  useEffect(() => {
    if (valores.is_parcelado === 'true' && parcelas.length > 0) {
      const valorTotal = formatarMonetárioParaNúmero(valor);

      const valores = calcularValoresParcelas(valorTotal, parcelas.length);

      const novasParcelas = parcelas.map((parcela, index) => ({
        ...parcela,
        valor_parcela: index === parcelas.length - 1
          ? formatarInputValorMonetário(valores.valorUltimaParcela)
          : formatarInputValorMonetário(formatarDecimalNumber(valores.valorParcela))
      }));

      atualizarParcelas(novasParcelas)
    }
  }, [valor]);
  //

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{xs: 5}}>
          <Controller
            name="step02.data_emissao"
            control={control}
            rules={{ required: 'Campo obrigatório',
              validate: async (value) => {
                            if (!value) return true;
              
                            try {
                              const dataMoment = moment(value, "YYYY-MM-DD");
                              if (!dataMoment.isValid()) {
                                return "Data inválida";
                              }

                              const dataVencimento = getValues(`step02.data_vencimento`);
                              if (dataVencimento) {
                                const dataVencimentoMoment = moment(dataVencimento, "YYYY-MM-DD");
                                if (dataMoment.isAfter(dataVencimentoMoment)) {
                                  return "Data de emissão não pode ser maior que a data de vencimento";
                                }
                              }
              
                              const response = await validacaoData(dataMoment);
              
                              if (response?.data === false) {
                                return response?.data?.message || "Data inválida";
                              }
              
                              return true;
                            } catch (error: any) {
                              console.error("Erro ao validar a data:", error);
              
                              if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
                                return "Erro de conexão. Verifique sua internet.";
                              }
              
                              return error?.response?.data?.message || "Erro ao validar a data";
                            }
                          }
             }}
            render={({ field, fieldState: { error, invalid } }) => (
              <BasicDatePicker
                label="Emissão"
                required
                helperText={error ? error.message : ""}
                error={invalid}
                value={field.value}
                onChange={(data) => field.onChange(data)}
                formatString="DD/MM/YYYY"
              />
            )}
          />
        </Grid>

        <Grid size={{xs: 5}}>
          <Controller
            name="step02.data_vencimento"
            control={control}
            rules={{ required: valores.is_parcelado !== 'true' ? 'Campo obrigatório' : false }}
            render={({ field, fieldState: { error, invalid } }) => (
              <BasicDatePicker
                disabled={valores.is_parcelado === 'true' ? true : false}
                label="Vencimento *"
                blockPastDates
                helperText={error ? error.message : ""}
                error={invalid}
                pastDates={valores.data_emissao}
                value={field.value}
                onChange={(data) => {
                  field.onChange(data);
                  handleAlterarDataVencimento(0, data)
                }}
                formatString="DD/MM/YYYY"
              />
            )}
          />
        </Grid>

        <Grid size={{xs: 5}}>
          <Controller
            name="step02.valor_total"
            control={control}
            render={({ field, fieldState: { error, invalid } }) => (
              <TextField
                label="Valor"
                required
                fullWidth
                error={invalid}
                helperText={error ? error.message : ""}
                value={field.value}
                onChange={(e) => {
                  const formattedValue = formatarInputValorMonetário(e.target.value)
                  field.onChange(formattedValue)
                  field.onChange(formattedValue);
                  handleAlterarValorParcela(0, formattedValue)
                }}
              />
            )}
          />
        </Grid>

        <Grid size={{xs: 5}}>
          <Controller
            name="step02.is_parcelado"
            control={control}
            rules={{ required: 'Campo obrigatório' }}
            render={({ field }) => (
              <FormControl {...field} fullWidth>
                <FormLabel id={`parcelar-radio`}>Parcelar valor?*</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="radioSimOuNao"
                  value={field.value}
                  onChange={({ target }) => {
                    if (target.value === 'true' && parcelas.length <= 1) {
                      setValue(
                        'step02.parcelas',
                        [{ numero_parcela: 1, data_vencimento: valores.data_vencimento, valor_parcela: valor }]
                      )
                    }
                    field.onChange(target.value)
                  }}
                >
                  {
                    valoresSelect.map((opcao, index) => (
                      <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                    ))
                  }
                </RadioGroup>
              </FormControl>
            )}
          />

        </Grid>
        {
          valores.is_parcelado === 'true' &&
          <Grid container spacing={2} size={{xs: 12}} pb={2}>
            <Grid size={{xs: 2}}>
              <TextField
                fullWidth
                label="Quantidade"
                value={gerarParcelas.quantidade}
                type="number"
                onChange={({ target }) => handleChangeGerarParcelaInput('quantidade', target.value)}
              />
            </Grid>

            <Grid size={{xs: 3}}>
              <BasicDatePicker
                label="Data primeira parcela"
                blockPastDates
                pastDates={valores.data_emissao}
                value={gerarParcelas.data_inicial}
                onChange={(data) => handleChangeGerarParcelaInput('data_inicial', data)}
                formatString="DD/MM/YYYY"
              />
            </Grid>

            <Grid size={{xs: 3}}>
              <TextField
                fullWidth
                value={gerarParcelas.intervalo}
                label="Intervalo entre parcelas"
                type="number"
                onChange={({ target }) => handleChangeGerarParcelaInput('intervalo', target.value)}
              />
            </Grid>

            <Grid size={{xs: 2}} display={'flex'} alignItems={'center'}>
              <Button
                fullWidth
                color="primary"
                variant="contained"
                sx={{ height: '100%' }}
                disabled={Number(gerarParcelas.quantidade) <= 1 ? true : false}
                onClick={() => handleGerarParcelas(gerarParcelas)}
              >
                Gerar
              </Button>
            </Grid>
          </Grid>
        }
        {
          valores.is_parcelado === 'true' && parcelas.length > 1 &&
          parcelas.map((parcela, index) => {
            return (
              <Grid container size={{xs: 12}} key={index} spacing={2}>
                <Grid size={{xs: 5}}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'center'} gap={2}>
                    <Typography fontWeight={'bold'} variant="h6">{index + 1}°)</Typography>
                    <Controller
                      name={`step02.parcelas[${index}].data_vencimento`}
                      rules={{ required: 'Campo obrigatório' }}
                      control={control}
                      render={({ field, fieldState: { error, invalid } }) => (
                        <BasicDatePicker
                          label="Vencimento *"
                          blockPastDates
                          pastDates={index === 0 ? valores.data_emissao : parcelas[index - 1]?.data_vencimento}
                          value={parcela.data_vencimento}
                          onChange={(data) => handleAlterarDataVencimento(index, data)}
                          formatString="DD/MM/YYYY"
                        />
                      )}
                    />
                  </Box>
                </Grid>

                <Grid size={{xs: 5}}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'center'} gap={2}>
                    <Controller
                      name={`step02.parcelas[${index}].valor_parcela`}
                      rules={{ required: 'Campo obrigatório' }}
                      control={control}
                      render={({ field, fieldState: { error, invalid } }) => (
                        <TextField
                          label={`Valor`}
                          required
                          value={parcela.valor_parcela}
                          fullWidth
                          onChange={(e) => {
                            const formattedValue = formatarInputValorMonetário(e.target.value)
                            handleAlterarValorParcela(index, formattedValue);
                          }}
                        />
                      )}
                    />

                    {
                      parcela.numero_parcela !== parcelas.length &&
                      <IconButton aria-label="delete" onClick={() => handleDeletarParcela()}>
                        <DeleteIcon />
                      </IconButton>
                    }
                    {
                      parcela.numero_parcela === parcelas.length &&
                      <IconButton aria-label="delete" onClick={() => handleAdicionarParcela()}>
                        <AddIcon />
                      </IconButton>
                    }
                  </Box>
                </Grid>
              </Grid>
            )
          })
        }

        <Controller
          name="valores.parcelas"
          control={control}
          render={({ fieldState: { error, invalid } }) => {
            if (!invalid) return <></>
            return (
              <Grid container spacing={2} my={1}>
                <Grid size={{xs: 10}}>
                  <Alert severity="warning">{error?.message}</Alert>
                </Grid>
              </Grid>
            )
          }}
        />

        <Grid size={{xs: 12}}>
          <Box display={'flex'} gap={2}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={isLoadingValidacaoData}
            >
              Voltar
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit(handleNextStep)}
              disabled={isLoadingValidacaoData}
            >
              {
                isLoadingValidacaoData &&
                <CircularProgress size={15} sx={{ marginRight: 1 }} />
              }
              Avançar
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  )
}

type TInternFilters = {
  [key: string]: any;
}

type TParcela = {
  numero_parcela: number,
  valor_parcela: string,
  data_vencimento: Moment,
}

type TObjParcelas = {
  quantidade: string;
  intervalo: string;
  data_inicial: Moment | null;
}

function calcularValoresParcelas(valorTotal: number, arrayLenght: number) {
  const valorParcela = Math.floor((valorTotal / arrayLenght) * 100) / 100;
  const valorUltimaParcela = formatarDecimalNumber(valorParcela + (valorTotal - (valorParcela * arrayLenght)));
  const valorParcelaFormatado = formatarInputValorMonetário(formatarDecimalNumber(valorParcela))
  return { valorParcela, valorUltimaParcela, valorParcelaFormatado }
}